/**
 * One scannable worklist table. Rows are dynamic (ZPCD views), so columns are resolved defensively
 * via pickString/pickNumber against the field names each view is known to expose. A row click drills
 * to that account's Customer-360. The money column (renewal amount) is gated on canSeeMoney() AND
 * tolerates an absent field — a worklist-but-no-money role simply doesn't get the column or the value.
 */

import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Table,
  type Column,
  ErrorState,
  EmptyState,
  SkeletonRows,
  StatusPill,
} from "@/components/ui";
import { useRbac } from "@/context/RbacContext";
import { RetiredBadge } from "@/components/portal/RetiredBadge";
import { formatDate, formatMoney } from "@/lib/format";
import { planName } from "@/lib/planNames";
import type { WorklistKind, WorklistRow } from "@/api/types";
import { UtilizationBar, utilizationTone, bandFromPct } from "@/features/customer/display";
import { pickBoolean, pickNumber, pickString, useWorklist } from "./useWorklists";

interface WorklistTableProps {
  kind: WorklistKind;
}

/** EXACTLY false — an absent flag means "not confirmed retired", never "retired" (RetiredBadge's rule). */
function isRetiredRow(r: WorklistRow): boolean {
  return pickBoolean(r, "is_active") === false;
}

/**
 * Frozen-figure wrapper for a retired account's cells.
 *
 * The badge next to the account NAME says the account was delisted. On its own that fact does not travel
 * to the numbers in the same row: ZPCD stops refreshing an account the moment it drops off Zoom's list, so
 * a retired row's countdown, renewal date and amount are pre-retirement snapshots that will never update.
 * Rendered at full confidence ("in 15 days", "$1,200.00") next to an amber badge of the same tone as the
 * countdown pill itself, they read as live. Muting them — and saying why on hover — makes the row honest
 * without deleting information the CSM still needs. Active rows are untouched.
 */
function Frozen({ retired, children }: { retired: boolean; children: ReactNode }) {
  const { t } = useTranslation();
  if (!retired) return <>{children}</>;
  return (
    <span className="cursor-help opacity-60" title={t("retired.frozenCell")}>
      {children}
    </span>
  );
}

const NAME_KEYS = ["account_name", "name"];
const ID_KEYS = ["account_id", "id"];
const PLAN_KEYS = ["base_type", "plan"]; // base_type = REAL plan; never account_type
const UTIL_KEYS = ["plan_util_pct", "util_pct", "utilization_pct"];
const DAYS_KEYS = ["days_until_renewal", "days_to_renewal"];
const RENEWAL_DATE_KEYS = ["renewal_date", "next_invoice_date"];
const AMOUNT_KEYS = ["renewal_amount", "amount", "arr"];
const CURRENCY_KEYS = ["renewal_currency", "currency"];
const LEFT_LIST_KEYS = ["left_list_at"];

export function WorklistTable({ kind }: WorklistTableProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canSeeMoney } = useRbac();
  const wl = useWorklist(kind);

  if (wl.isPending) return <SkeletonRows rows={5} />;
  if (wl.isError) return <ErrorState error={wl.error} onRetry={() => void wl.refetch()} />;

  const rows = wl.data.rows;
  const hasMoney = canSeeMoney();

  // Whether THIS list's view exposes a column at all (so we don't render an all-dash column).
  const showUtil = rows.some((r) => pickNumber(r, UTIL_KEYS) != null);
  const showDays = rows.some((r) => pickNumber(r, DAYS_KEYS) != null);
  const showRenewalDate = rows.some((r) => pickString(r, RENEWAL_DATE_KEYS) != null);
  const showAmount = hasMoney && rows.some((r) => pickNumber(r, AMOUNT_KEYS) != null);

  const columns: Column<WorklistRow>[] = [
    {
      key: "account",
      header: t("worklist.col.account"),
      // Retired accounts are BADGED here, never hidden. renewal / churn / trial are the three lists whose
      // job is to warn you about money on its way out — filtering a retired account out of them would
      // delete the warning instead of explaining it. (is_active / left_list_at come from the view; the
      // sell-more lists filter these accounts out in SQL instead, so the badge never appears there.)
      cell: (r) => (
        <span className="flex items-center gap-2">
          <span className="font-medium text-(--text)">
            {pickString(r, NAME_KEYS) ?? pickString(r, ID_KEYS) ?? "—"}
          </span>
          <RetiredBadge
            is_active={pickBoolean(r, "is_active")}
            left_list_at={pickString(r, LEFT_LIST_KEYS)}
            compact
          />
        </span>
      ),
      sortValue: (r) => pickString(r, NAME_KEYS),
    },
    {
      key: "plan",
      header: t("customer.plan"),
      cell: (r) => planName(pickString(r, PLAN_KEYS)),
      sortValue: (r) => planName(pickString(r, PLAN_KEYS)),
    },
    ...(showUtil
      ? [
          {
            key: "util",
            header: t("customer.utilization"),
            cell: (r: WorklistRow) => {
              const pct = pickNumber(r, UTIL_KEYS);
              return (
                <Frozen retired={isRetiredRow(r)}>
                  <UtilizationBar
                    pct={pct}
                    tone={utilizationTone(bandFromPct(pct))}
                    className="w-36"
                  />
                </Frozen>
              );
            },
            sortValue: (r: WorklistRow) => pickNumber(r, UTIL_KEYS),
          } satisfies Column<WorklistRow>,
        ]
      : []),
    ...(showDays
      ? [
          {
            key: "days",
            header: t("worklist.col.daysUntilRenewal"),
            cell: (r: WorklistRow) => {
              const days = pickNumber(r, DAYS_KEYS);
              if (days == null) return "—";
              const retired = isRetiredRow(r);
              // A retired account's countdown is counting down to a renewal that will never happen, so
              // it must not glow urgent (warn/danger) as if it were a live deadline — the urgency the
              // CSM needs is "this account is GONE", which the badge already carries. Neutral + muted.
              if (days < 0) {
                return (
                  <Frozen retired={retired}>
                    <StatusPill tone={retired ? "neutral" : "danger"}>
                      {t("worklist.col.overdue", { days: -days })}
                    </StatusPill>
                  </Frozen>
                );
              }
              // Only the trial list can go negative — a trial Zoom still reports active past its end
              // date. "in -52 days" is nonsense and a warn pill understates it; render it as overdue.
              return (
                <Frozen retired={retired}>
                  <StatusPill
                    tone={
                      retired ? "neutral" : days <= 30 ? "warn" : days <= 60 ? "info" : "neutral"
                    }
                  >
                    {t("customer.daysUntilRenewal", { days })}
                  </StatusPill>
                </Frozen>
              );
            },
            sortValue: (r: WorklistRow) => pickNumber(r, DAYS_KEYS),
            align: "right" as const,
          } satisfies Column<WorklistRow>,
        ]
      : []),
    ...(showRenewalDate
      ? [
          {
            key: "renewal_date",
            // The trial list reuses the renewal_date column for the trial's END date — same shape, very
            // different meaning; label it honestly rather than calling a trial expiry a "Renewal".
            header: kind === "trial_expiring" ? t("worklist.col.trialEnds") : t("customer.renewal"),
            cell: (r: WorklistRow) => (
              <Frozen retired={isRetiredRow(r)}>
                {formatDate(pickString(r, RENEWAL_DATE_KEYS))}
              </Frozen>
            ),
            sortValue: (r: WorklistRow) => pickString(r, RENEWAL_DATE_KEYS),
          } satisfies Column<WorklistRow>,
        ]
      : []),
    ...(showAmount
      ? [
          {
            key: "amount",
            header: t("customer.money.renewalAmount"),
            cell: (r: WorklistRow) => (
              <Frozen retired={isRetiredRow(r)}>
                {formatMoney(pickNumber(r, AMOUNT_KEYS), pickString(r, CURRENCY_KEYS)) ?? "—"}
              </Frozen>
            ),
            sortValue: (r: WorklistRow) => pickNumber(r, AMOUNT_KEYS),
            align: "right" as const,
            numeric: true,
          } satisfies Column<WorklistRow>,
        ]
      : []),
  ];

  function rowKeyOf(r: WorklistRow): string {
    return pickString(r, ID_KEYS) ?? pickString(r, NAME_KEYS) ?? JSON.stringify(r);
  }

  function onRowClick(r: WorklistRow) {
    const id = pickString(r, ID_KEYS);
    if (id) navigate(`/customers/${encodeURIComponent(id)}`);
  }

  const table = (
    <Table
      columns={columns}
      rows={rows}
      rowKey={rowKeyOf}
      caption={t(`worklist.${kind}`)}
      onRowClick={onRowClick}
      empty={<EmptyState title={t("worklist.empty")} body={t("worklist.emptyBody")} />}
    />
  );

  // The money column is gated at source: showAmount requires canSeeMoney() (line above), and the server
  // strips renewal_amount for a non-money role regardless. No MoneyGate wrapper here — a wrapper whose
  // fallback equals its children gates nothing and only misleads a future reader into thinking it does.
  return table;
}
